import React, { createContext, useContext, useState, useEffect } from 'react';

const storageKey = 'accessibilitySettings';
const fontSteps = [0.5, 0.75, 1, 1.5, 2];

const defaultStepIndex = 2;
const defaultTheme = 'system';
const defaultFont = 'default';
const defaultPopup = false;
const defaultAutoplay = false;

const AccessibilityContext = createContext(null);

const getStoredSettings = () => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(storageKey)) || {};
  } catch {
    return {};
  }
};

const setStoredSettings = (updates) => {
  if (typeof window === 'undefined') return;
  const existing = getStoredSettings();
  const merged = { ...existing, ...updates };
  localStorage.setItem(storageKey, JSON.stringify(merged));
};

export const AccessibilityProvider = ({ children }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(defaultStepIndex);
  const [isFontChanged, setIsFontChanged] = useState(false);
  const [theme, setTheme] = useState('system');
  const [resolvedTheme, setResolvedTheme] = useState('light'); // actual applied theme
  const [showAccessibilityPopup, setshowAccessibilityPopup] = useState(false);
  const [isAutoplay, setIsAutoplay] = useState(false);
  useEffect(() => {
    const { fontStepIndex, theme: savedTheme, dialogFont, autoplay } = getStoredSettings();

    if (
      typeof fontStepIndex === 'number' &&
      fontStepIndex >= 0 &&
      fontStepIndex < fontSteps.length
    ) {
      setCurrentStepIndex(fontStepIndex);
    }

    if (dialogFont === 'system') {
      setIsFontChanged(true);
    } else {
      setIsFontChanged(false);
    }
    if (typeof autoplay === 'boolean') {
      setIsAutoplay(autoplay);
    }

    const themeToApply = savedTheme || 'system';
    setTheme(themeToApply);
    applyTheme(themeToApply);

    if (themeToApply === 'system') {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const applySystemTheme = () => applyTheme('system');
      media.addEventListener('change', applySystemTheme);
      return () => media.removeEventListener('change', applySystemTheme);
    }
  }, []);

  useEffect(() => {
    setStoredSettings({ autoplay: isAutoplay });
  }, [isAutoplay]);

  useEffect(() => {
    setStoredSettings({ fontStepIndex: currentStepIndex });
  }, [currentStepIndex]);

  useEffect(() => {
    const dialogFont = isFontChanged ? 'system' : 'default';
    setStoredSettings({ dialogFont: dialogFont });
  }, [isFontChanged]);

  // ---------------- font controls ----------------
  const increaseFont = (callback) => {
    setCurrentStepIndex((prev) => (prev < fontSteps.length - 1 ? prev + 1 : prev));
    if (callback) callback();
  };

  const decreaseFont = (callback) => {
    setCurrentStepIndex((prev) => (prev > 0 ? prev - 1 : prev));
    if (callback) callback();
  };

  const resetFont = (callback) => {
    setCurrentStepIndex(defaultStepIndex);
    if (callback) callback();
  };

  const changeFonts = (callback) => {
    setIsFontChanged((prev) => !prev);
    if (callback) callback();
  };

  // ---------------- theme controls ----------------
  const applyTheme = (selectedTheme) => {
    let finalTheme = selectedTheme;

    if (selectedTheme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      finalTheme = prefersDark ? 'dark' : 'light';
    }

    setResolvedTheme(finalTheme);
    document.documentElement.setAttribute('data-theme', finalTheme);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setStoredSettings({ theme: newTheme });
    applyTheme(newTheme);
  };

  // ---------------- Accessibility Popup controls ----------------
  const handleAccessibilityPopup = (callback) => {
    const storedSettings = getStoredSettings();
    if (!storedSettings.accessibilityPopup) {
      setshowAccessibilityPopup(true);

      const updatedSettings = {
        ...storedSettings,
        accessibilityPopup: true,
      };
      setStoredSettings(updatedSettings);
    } else {
      callback?.(100);
    }
  };

  const continueButton = (callback) => {
    setshowAccessibilityPopup(false);
    callback?.(100);
  };

  const resetAccessibilitySettings = (option = 'all', callback) => {
    let updates = {};

    switch (option) {
      case 'fontSize':
        setCurrentStepIndex(defaultStepIndex);
        updates.fontStepIndex = defaultStepIndex;
        break;

      case 'font':
        setIsFontChanged(false);
        updates.dialogFont = defaultFont;
        break;

      case 'theme':
        handleThemeChange(defaultTheme);
        updates.theme = defaultTheme;
        break;

      case 'autoplay':
        setIsAutoplay(defaultAutoplay);
        updates.autoplay = defaultAutoplay;
        break;

      case 'all':
      default:
        setCurrentStepIndex(defaultStepIndex);
        setIsFontChanged(false);
        setTheme(defaultTheme);
        applyTheme(defaultTheme);
        setIsAutoplay(defaultAutoplay);
        updates = {
          fontStepIndex: defaultStepIndex,
          dialogFont: defaultFont,
          accessibilityPopup: defaultPopup,
          theme: defaultTheme,
          autoplay: defaultAutoplay,
        };
        break;
    }

    if (Object.keys(updates).length > 0) {
      setStoredSettings(updates);
    }

    callback?.();
  };

  //  ---------------- Autoplay ----------------
  const toggleAutoplay = (value, episodeName) => {
    setIsAutoplay(value);
    setStoredSettings({ autoplay: value });

    const eventTracking = {
      event: `autoplay_${value ? 'enable' : 'disable'}`,
      actionType: 'internal',
      clickText: 'Autoplay',
      loginStatus: 'logged-in',
      episodeName: episodeName,
      lnPagelocation: 'bottom',
      moduleName: 'Child Dashboard - Engagement',
      pageName: 'Kids Dashboard',
      rollType: 'kids',
      status: `autoplay ${value ? 'enabled' : 'disabled'}`,
    };

    window.dataLayer?.push(eventTracking);
  };

  const value = {
    fontScale: fontSteps[currentStepIndex],
    currentStepIndex,
    increaseFont,
    decreaseFont,
    resetFont,
    fontSteps,
    changeFonts,
    isFontChanged,
    changeTheme: handleThemeChange,
    setTheme: handleThemeChange,
    theme, // user’s selected preference ("system" | "light" | "dark")
    resolvedTheme, // actual system-applied theme ("light" | "dark")
    showAccessibilityPopup,
    handleAccessibilityPopup,
    continueButton,
    resetAccessibilitySettings,
    isAutoplay,
    toggleAutoplay,
  };

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};
